import type { TFunction } from 'i18next';
import { formatMoney } from '@/api/shop';

const SEPARATOR = ' · ';

// formatActivitySummary returns a localized one-liner for an audit log
// entry when the action has richer client-side rendering than the
// generic `t(settings.activity.action.${action}.summary, meta)` flow can
// express. Returns null for actions that don't need special handling —
// Activity.tsx then falls back to the i18n template + raw metadata.
export function formatActivitySummary(
  t: TFunction,
  action: string,
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  switch (action) {
    case 'media_asset_upload':
    case 'media_asset_delete':
      return formatMediaSummary(t, action, meta, locale);
    case 'gettingstarted_milestone_unlock':
      return formatMilestoneSummary(t, meta, locale);
    case 'notice_send':
      return formatNoticeSendSummary(t, meta, locale);
    case 'notice_run_complete':
      return formatNoticeRunCompleteSummary(t, meta, locale);
    case 'fee_rule_create':
    case 'fee_rule_update':
    case 'fee_rule_soft_delete':
      return formatFeeRuleSummary(t, action, meta, locale);
    default:
      return null;
  }
}

// formatFeeRuleSummary names the rule a fee row touched. "Created a fee rule"
// told the reader nothing — a club has dozens and the id isn't placeable. The
// rule's own label wins when the author set one; otherwise we fall back to the
// scope it applies at (whole club / section / team / one person) plus the
// amount it sets. Returns null for rows written before this metadata existed so
// they keep their stored server summary.
function formatFeeRuleSummary(
  t: TFunction,
  action: string,
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  const level = typeof meta.level === 'string' ? meta.level : '';
  const label = typeof meta.label === 'string' ? meta.label.trim() : '';
  if (!level && !label) return null;

  const verb = t(`settings.activity.fee.verb.${feeVerb(action)}`);
  // Most specific wins: the author's label, then the concrete person or team
  // the API resolved from the rule's ids, then the generic scope.
  const memberName = typeof meta.member_name === 'string' ? meta.member_name.trim() : '';
  const teamName = typeof meta.team_name === 'string' ? meta.team_name.trim() : '';
  let subject: string;
  if (label) {
    subject = `“${truncate(label, 60)}”`;
  } else if (meta.club_membership === true) {
    subject = t('settings.activity.fee.clubMembership');
  } else if (memberName) {
    subject = t('settings.activity.fee.forMember', { name: memberName });
  } else if (teamName) {
    subject = t('settings.activity.fee.forTeam', { name: teamName });
  } else {
    subject = t(`settings.activity.fee.level.${level}`, { defaultValue: '' });
    if (!subject) return null;
  }

  const parts = [`${verb} ${subject}`];
  const amount = feeAmount(t, meta, locale);
  if (amount) parts.push(amount);
  return parts.join(SEPARATOR);
}

function feeVerb(action: string): 'create' | 'update' | 'delete' {
  if (action === 'fee_rule_create') return 'create';
  if (action === 'fee_rule_soft_delete') return 'delete';
  return 'update';
}

// feeAmount renders the money the rule sets, or the exemption it grants. A rule
// always resolves at least one of the two (the server rejects "dead" rules), so
// this is the single most useful thing to put after the name.
function feeAmount(
  t: TFunction,
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  const currency = typeof meta.currency === 'string' ? meta.currency : 'SEK';
  const bits: string[] = [];
  if (typeof meta.membership_minor === 'number') {
    bits.push(
      t('settings.activity.fee.membership', {
        amount: formatMoney(meta.membership_minor, currency, locale || 'sv-SE'),
      }),
    );
  }
  if (typeof meta.training_minor === 'number') {
    bits.push(
      t('settings.activity.fee.training', {
        amount: formatMoney(meta.training_minor, currency, locale || 'sv-SE'),
      }),
    );
  }
  if (meta.exempt_membership === true) bits.push(t('settings.activity.fee.exemptMembership'));
  if (meta.exempt_training === true) bits.push(t('settings.activity.fee.exemptTraining'));
  return bits.length > 0 ? bits.join(SEPARATOR) : null;
}

// formatNoticeSendSummary turns a per-member notice send into a specific
// one-liner — "Skickade avisering på 500 kr till Anna Svensson · F09" — instead
// of the generic "Skickade en avisering" that made the feed a wall of identical
// rows. Returns null for pre-enrichment rows (no member_name) so they fall back
// to the stored server summary.
function formatNoticeSendSummary(
  t: TFunction,
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  const member = typeof meta.member_name === 'string' ? meta.member_name.trim() : '';
  if (!member) return null;
  const amountMinor = typeof meta.amount_minor === 'number' ? meta.amount_minor : 0;
  const currency = typeof meta.currency === 'string' ? meta.currency : 'SEK';
  const team = typeof meta.team_name === 'string' ? meta.team_name.trim() : '';

  const amount = formatMoney(amountMinor, currency, locale || 'sv-SE');
  const head = t('settings.activity.notice.send', { amount, member });
  return team ? `${head}${SEPARATOR}${team}` : head;
}

// formatNoticeRunCompleteSummary names the batch outcome — how many notices went
// out and, once delivery is wired, the push/email split — so the bookend row of
// a batch run answers "what actually happened / did emails go out" at a glance.
function formatNoticeRunCompleteSummary(
  t: TFunction,
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  const created = typeof meta.notices_created === 'number' ? meta.notices_created : null;
  if (created === null) return null;
  const n = new Intl.NumberFormat(locale || undefined).format(created);
  const head = t('settings.activity.notice.runComplete', { n });

  const push = typeof meta.push_sent === 'number' ? meta.push_sent : null;
  const emails = typeof meta.emails_sent === 'number' ? meta.emails_sent : null;
  if (push === null && emails === null) return head;
  const split = t('settings.activity.notice.runDeliverySplit', {
    push: push ?? 0,
    emails: emails ?? 0,
  });
  return `${head}${SEPARATOR}${split}`;
}

// Growth families whose tier values are money in minor units (öre/cents) and
// must be divided by 100 before display. Mirrors gettingstarted.UnitMoneyMinor.
const GROWTH_MONEY_FAMILIES = new Set(['payments_collected']);
const GROWTH_FAMILIES = new Set([
  'members_count',
  'payments_collected',
  'activities_count',
  'broadcasts_count',
]);

// formatMilestoneSummary turns a getting-started unlock row into a specific
// one-liner naming WHICH milestone was reached, instead of the generic
// "Reached a getting-started milestone". The stored metadata carries
// milestone_key, which is either a setup key (first_team, branding, …) or a
// growth tier "<family>:<threshold>" (members_count:100). Setup titles reuse
// the getting-started dialog's labels (gettingStarted.milestone.<key>.title);
// growth rows reuse the family label + a locale-formatted threshold. Returns
// null for rows that predate this metadata so they fall back to the generic
// server summary.
function formatMilestoneSummary(
  t: TFunction,
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  const key = typeof meta.milestone_key === 'string' ? meta.milestone_key : '';
  if (!key) return null;

  const sep = key.indexOf(':');
  if (sep > -1) {
    const family = key.slice(0, sep);
    const threshold = Number(key.slice(sep + 1));
    if (!GROWTH_FAMILIES.has(family) || !Number.isFinite(threshold)) return null;
    const familyLabel = t(`gettingStarted.growth.${family}`);
    const n = GROWTH_MONEY_FAMILIES.has(family) ? Math.floor(threshold / 100) : threshold;
    const value = new Intl.NumberFormat(locale || undefined).format(n);
    return t('settings.activity.milestone.growth', { family: familyLabel, value });
  }

  const title = t(`gettingStarted.milestone.${key}.title`, { defaultValue: '' });
  if (!title) return null;
  return t('settings.activity.milestone.setup', { title });
}

function formatMediaSummary(
  t: TFunction,
  action: 'media_asset_upload' | 'media_asset_delete',
  meta: Record<string, unknown>,
  locale: string,
): string | null {
  const kind = typeof meta.kind === 'string' ? meta.kind : '';
  const contentType = typeof meta.content_type === 'string' ? meta.content_type : '';
  const byteSize = typeof meta.byte_size === 'number' ? meta.byte_size : 0;
  const alt = typeof meta.alt === 'string' ? meta.alt.trim() : '';
  const width = typeof meta.width === 'number' ? meta.width : undefined;
  const height = typeof meta.height === 'number' ? meta.height : undefined;

  // Pre-enrichment rows (before this code shipped) have no kind/content_type.
  // Fall through to the i18n template so they still render their stored
  // server-side English summary.
  if (!kind && !contentType) return null;

  const kindLabel = mediaKindLabel(t, kind, contentType);
  const verbKey =
    action === 'media_asset_upload'
      ? 'settings.activity.media.verb.upload'
      : 'settings.activity.media.verb.delete';
  const verb = t(verbKey);

  let head = `${verb} ${kindLabel}`;
  if (alt) head += ` “${truncate(alt, 60)}”`;

  const parts = [head];
  if (byteSize > 0) parts.push(formatBytes(byteSize, locale));
  if (width && height) parts.push(`${width}×${height}`);
  return parts.join(SEPARATOR);
}

function mediaKindLabel(t: TFunction, kind: string, contentType: string): string {
  const ctKey = contentTypeKey(contentType);
  if (ctKey) {
    return t(`settings.activity.media.kind.${ctKey}`);
  }
  if (kind === 'image' || kind === 'document' || kind === 'video') {
    return t(`settings.activity.media.kind.${kind}`);
  }
  return t('settings.activity.media.kind.file');
}

function contentTypeKey(ct: string): string | null {
  switch (ct) {
    case 'image/jpeg':
      return 'jpeg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    case 'application/pdf':
      return 'pdf';
    default:
      return null;
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max) + '…';
}

// formatBytes renders the shortest readable byte count: "812 B" / "2.3 KB"
// / "1.4 MB" / "1.4 GB". Uses locale-aware decimal separator so Nordic
// locales render "2,3 MB" while en uses "2.3 MB".
function formatBytes(n: number, locale: string): string {
  const k = 1024;
  if (n < k) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / k;
  let i = 0;
  while (v >= k && i < units.length - 1) {
    v /= k;
    i++;
  }
  const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
  const formatted = new Intl.NumberFormat(locale || undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
  return `${formatted} ${units[i]}`;
}
